<template>
    <div class="upload-document-box">
        <DocumentCard
            v-bind="documentCardProps"
            @updated:document="onDocumentoSeleccionado"
            @added:document="onDocumentoSeleccionado"
            ref="documentCardRef"
        />
    </div>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, ref, computed } from 'vue';
import { modalDefinedEmits, type ToolbarReference, useModalBuilder } from '@helpers/modal-building-helper';
import { messageBoxResult } from '@components/dialogs/message-box';
import { DxButton } from 'devextreme-vue/button';
import DocumentCard from '@components/document-card/DocumentCard.vue';
import type { IDocumentCardProps, IDocumentInitialState } from '@components/document-card/models/IDocumentCardProps';
import type { CardFile } from '@components/document-card/models/CardFile';
import type { IUploadDocumentBoxProps } from '@components/dialogs/models/IUploadDocumentBoxProps';
import { uniqueId } from 'lodash';

const documentoASubir = ref<File>();
const documentCardRef = ref<InstanceType<typeof DocumentCard>>();

const props = withDefaults(defineProps<IUploadDocumentBoxProps>(), {
  title: '',
  required: false,
  documentUri: undefined,
  allowedFileFormats: () => ['all'],
  templateUri: undefined,
});

const initialDocumentList = computed<IDocumentInitialState[]>(() => {
  if (!props.documentUri) {
    return [];
  }
  
  return [{
    id: uniqueId(),
    uri: props.documentUri,
    fileName: props.documentUri.split('/').pop() ?? '',
    description: undefined
  }];
});

const documentCardProps = computed<IDocumentCardProps>(() => {
  return {
    title: props.title,
    required: props.required,
    actions: ['open', 'download', 'add', 'replace'],
    maxFiles: 1,
    allowedFileFormats: props.allowedFileFormats,
    align: 'horizontal',
    initialDocumentList: initialDocumentList.value,
    multiple: false,
    templateUri: props.templateUri
  };
});

onMounted(() => {
    const modal = useModalBuilder();
    modal.setContext(getCurrentInstance()!);

    configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
    let okButton: ToolbarReference<DxButton>;
    let cancelButton: ToolbarReference<DxButton>;

    ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
    okButton.update(w => w.onClick = async () => {
        const validationResult = documentCardRef.value?.isValid ?? false;
        
        if (validationResult) {
            modal.close({ result: messageBoxResult.ok, value: documentoASubir.value });
        }
    });
    cancelButton.update(w => w.onClick = () => modal.close({ result: messageBoxResult.cancel }));
}

function onDocumentoSeleccionado(file: CardFile) {
    documentoASubir.value = file?.file ?? undefined;
}

defineEmits([...modalDefinedEmits]);
</script>

<style scoped lang="scss">
.upload-document-box {
    display: flex;
    justify-content: center;
}
</style>
